// EPSE Energy Balance Calculation Engine
// Calculates energy balance with grid, PV, battery, and shortfall

import type { SyntheticProfileConfig, BESSConfig } from '@/types/epse';

export interface EnergyBalancePoint {
  quarter: number;
  time: string;
  hour: number;
  
  // Vraagzijde
  demand_kw: number;
  
  // Aanbodzijde
  grid_max_kw: number;
  grid_actual_kw: number;
  pv_available_kw: number;
  pv_used_kw: number;
  
  // Tekort & Batterij
  shortfall_kw: number;
  battery_discharge_kw: number;
  battery_charge_kw: number;
  
  // Resultaat
  unmet_demand_kw: number;
  
  // SOC
  soc_kwh: number;
  soc_percent: number;
}

export interface EnergyBalanceSummary {
  // Dagelijkse totalen
  total_demand_kwh: number;
  grid_capacity_kwh: number;         // Max uit net per dag (limit × 24)
  actual_grid_kwh: number;
  pv_generated_kwh: number;
  pv_used_kwh: number;
  pv_curtailed_kwh: number;
  
  // Tekorten
  shortfall_kwh: number;             // Totaal tekort (vraag - net - PV)
  battery_supplied_kwh: number;      // Hoeveel batterij levert
  unmet_demand_kwh: number;          // Restant tekort
  
  // Overschrijdingen
  hours_above_limit: number;
  max_exceedance_kw: number;
  
  // Batterij stats
  battery_cycles: number;
  
  // Percentages
  self_consumption_pct: number;      // % eigen gebruik PV
  grid_dependency_pct: number;       // % van net
  battery_contribution_pct: number;  // % door batterij
}

export interface MonthlyBalance {
  month: number;
  monthName: string;
  grid_kwh: number;
  pv_kwh: number;
  battery_kwh: number;
  shortfall_kwh: number;
}

/**
 * Generate PV profile for a given hour and season
 * Returns kW per kWp installed
 */
export function getPVOutput(
  hour: number,
  season: 'winter' | 'lente' | 'zomer' | 'herfst',
  profileType: 'none' | 'east_west' | 'south'
): number {
  if (profileType === 'none') return 0;
  
  // Seasonal factors
  const seasonFactors = {
    winter: 0.3,
    lente: 0.7,
    zomer: 1.0,
    herfst: 0.5,
  };
  
  // PV curves (normalized)
  const southCurve: Record<number, number> = {
    0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0,
    6: 0.05, 7: 0.15, 8: 0.35, 9: 0.55, 10: 0.75, 11: 0.90,
    12: 1.0, 13: 0.95, 14: 0.85, 15: 0.70, 16: 0.50, 17: 0.30,
    18: 0.15, 19: 0.05, 20: 0, 21: 0, 22: 0, 23: 0,
  };
  
  const eastWestCurve: Record<number, number> = {
    0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0,
    6: 0.10, 7: 0.30, 8: 0.50, 9: 0.65, 10: 0.75, 11: 0.80,
    12: 0.80, 13: 0.80, 14: 0.75, 15: 0.65, 16: 0.50, 17: 0.35,
    18: 0.20, 19: 0.10, 20: 0, 21: 0, 22: 0, 23: 0,
  };
  
  const curve = profileType === 'south' ? southCurve : eastWestCurve;
  const hourInt = Math.floor(hour);
  
  return (curve[hourInt] || 0) * seasonFactors[season];
}

/**
 * Calculate full energy balance for a daily profile
 */
export function calculateEnergyBalance(
  demandProfile: number[],
  config: SyntheticProfileConfig,
  bessConfig: BESSConfig,
  season: 'winter' | 'lente' | 'zomer' | 'herfst' = 'winter'
): { points: EnergyBalancePoint[]; summary: EnergyBalanceSummary } {
  const connectionLimit = config.connection_kw_limit;
  const pvCapacity = config.pv_capacity_kwp || 0;
  const pvType = config.pv_profile_type || 'none';
  
  // BESS state
  let soc = bessConfig.soc_initial * bessConfig.capacity_kwh;
  const socMin = bessConfig.soc_min * bessConfig.capacity_kwh;
  const socMax = bessConfig.soc_max * bessConfig.capacity_kwh;
  
  const points: EnergyBalancePoint[] = [];
  
  // Totals
  let totalDemand = 0;
  let totalGridActual = 0;
  let totalPVGenerated = 0;
  let totalPVUsed = 0;
  let totalShortfall = 0;
  let totalBatteryDischarge = 0;
  let totalBatteryCharge = 0;
  let totalUnmet = 0;
  let hoursAboveLimit = 0;
  let maxExceedance = 0;
  
  demandProfile.forEach((demand, i) => {
    const hour = (i % 96) / 4;
    const hourInt = Math.floor(hour);
    const minute = ((i % 96) % 4) * 15;
    
    // 1. Calculate PV output
    const pvAvailable = pvCapacity * getPVOutput(hour, season, pvType);
    
    // 2. Calculate how much grid can provide (max = connection limit)
    const gridMax = connectionLimit;
    
    // 3. Calculate demand after PV
    const demandAfterPV = Math.max(0, demand - pvAvailable);
    const pvUsed = Math.min(pvAvailable, demand);
    
    // 4. Calculate what grid actually provides (capped at limit)
    const gridActual = Math.min(demandAfterPV, gridMax);
    
    // 5. Calculate shortfall (demand that can't be met by grid + PV)
    const shortfall = Math.max(0, demandAfterPV - gridMax);
    
    // 6. Battery action
    let batteryDischarge = 0;
    let batteryCharge = 0;
    
    if (shortfall > 0 && soc > socMin) {
      // Discharge to cover shortfall
      const maxDischarge = Math.min(
        bessConfig.power_kw,
        (soc - socMin) / bessConfig.efficiency_discharge
      );
      batteryDischarge = Math.min(shortfall, maxDischarge);
      soc -= batteryDischarge * bessConfig.efficiency_discharge;
      totalBatteryDischarge += batteryDischarge * 0.25;
    } else if (shortfall === 0 && soc < socMax) {
      // Charge when there's headroom
      const headroom = gridMax - gridActual;
      const maxCharge = Math.min(
        bessConfig.power_kw,
        (socMax - soc) / bessConfig.efficiency_charge,
        headroom * 0.5
      );
      if (maxCharge > 0) {
        batteryCharge = maxCharge;
        soc += batteryCharge * bessConfig.efficiency_charge;
        totalBatteryCharge += batteryCharge * 0.25;
      }
    }
    
    // 7. Calculate unmet demand (shortfall not covered by battery)
    const unmet = Math.max(0, shortfall - batteryDischarge);
    
    // Track exceedances
    if (demand > connectionLimit) {
      hoursAboveLimit++;
      maxExceedance = Math.max(maxExceedance, demand - connectionLimit);
    }
    
    // Accumulate totals (kWh = kW * 0.25h)
    totalDemand += demand * 0.25;
    totalGridActual += (gridActual + batteryCharge) * 0.25;
    totalPVGenerated += pvAvailable * 0.25;
    totalPVUsed += pvUsed * 0.25;
    totalShortfall += shortfall * 0.25;
    totalUnmet += unmet * 0.25;
    
    points.push({
      quarter: i % 96,
      time: hourInt.toString(), // Simplified: just hour number (0-23)
      hour: hourInt,
      demand_kw: demand,
      grid_max_kw: gridMax,
      grid_actual_kw: gridActual + batteryCharge,
      pv_available_kw: pvAvailable,
      pv_used_kw: pvUsed,
      shortfall_kw: shortfall,
      battery_discharge_kw: batteryDischarge,
      battery_charge_kw: batteryCharge,
      unmet_demand_kw: unmet,
      soc_kwh: soc,
      soc_percent: (soc / bessConfig.capacity_kwh) * 100,
    });
  });
  
  const batteryCycles = (totalBatteryCharge + totalBatteryDischarge) / (2 * bessConfig.capacity_kwh);
  
  const summary: EnergyBalanceSummary = {
    total_demand_kwh: totalDemand,
    grid_capacity_kwh: connectionLimit * 24,
    actual_grid_kwh: totalGridActual,
    pv_generated_kwh: totalPVGenerated,
    pv_used_kwh: totalPVUsed,
    pv_curtailed_kwh: totalPVGenerated - totalPVUsed,
    shortfall_kwh: totalShortfall,
    battery_supplied_kwh: totalBatteryDischarge,
    unmet_demand_kwh: totalUnmet,
    hours_above_limit: hoursAboveLimit * 0.25,
    max_exceedance_kw: maxExceedance,
    battery_cycles: batteryCycles,
    self_consumption_pct: totalDemand > 0 ? (totalPVUsed / totalDemand) * 100 : 0,
    grid_dependency_pct: totalDemand > 0 ? (totalGridActual / totalDemand) * 100 : 0,
    battery_contribution_pct: totalDemand > 0 ? (totalBatteryDischarge / totalDemand) * 100 : 0,
  };
  
  return { points, summary };
}

/**
 * Calculate yearly summary by month
 */
export function calculateMonthlyBalance(
  config: SyntheticProfileConfig,
  bessConfig: BESSConfig,
  generateDailyProfile: (config: SyntheticProfileConfig, season: string, isWeekend: boolean) => number[]
): MonthlyBalance[] {
  const months: MonthlyBalance[] = [];
  
  const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
  const monthSeasons: Array<'winter' | 'lente' | 'zomer' | 'herfst'> = [
    'winter', 'winter', 'lente', 'lente', 'lente', 'zomer',
    'zomer', 'zomer', 'herfst', 'herfst', 'herfst', 'winter'
  ];
  const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  
  for (let m = 0; m < 12; m++) {
    const season = monthSeasons[m];
    const days = daysPerMonth[m];
    const weekdays = Math.round(days * 5 / 7);
    const weekendDays = days - weekdays;
    
    // Generate representative profiles
    const weekdayProfile = generateDailyProfile(config, season, false);
    const weekendProfile = generateDailyProfile(config, season, true);
    
    // Calculate balance for each
    const { summary: weekdaySummary } = calculateEnergyBalance(weekdayProfile, config, bessConfig, season);
    const { summary: weekendSummary } = calculateEnergyBalance(weekendProfile, config, bessConfig, season);
    
    months.push({
      month: m + 1,
      monthName: monthNames[m],
      grid_kwh: weekdaySummary.actual_grid_kwh * weekdays + weekendSummary.actual_grid_kwh * weekendDays,
      pv_kwh: weekdaySummary.pv_used_kwh * weekdays + weekendSummary.pv_used_kwh * weekendDays,
      battery_kwh: weekdaySummary.battery_supplied_kwh * weekdays + weekendSummary.battery_supplied_kwh * weekendDays,
      shortfall_kwh: weekdaySummary.unmet_demand_kwh * weekdays + weekendSummary.unmet_demand_kwh * weekendDays,
    });
  }
  
  return months;
}
